// src/components/Header.jsx
import React, { useEffect, useState, useRef, useCallback } from "react";
import { BsSearch, BsPersonCircle, BsBagDash } from "react-icons/bs";
import { FiMenu } from "react-icons/fi";
import { FaChevronDown, FaChevronRight, FaChevronLeft } from "react-icons/fa";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Currency from "./Currency";
import { useCart } from "../Context/CartContext";
import { useWishlist } from "../Context/WishlistContext";
import { useCurrency } from "../Context/CurrencyContext";
import {
  getCartAPI,
  updateCartItemAPI,
  removeCartItemAPI,
  clearCartAPI,
  syncCartAPI
} from "../pages/api";
import "./Header.css";

// ----------------------------------------------------------------------
//  Global cache manager – SINGLE SOURCE OF TRUTH
// ----------------------------------------------------------------------
class ApiCacheManager {
  static instance = null;

  constructor() {
    this.cache = {
      categories: null,
      subcategories: null,
      bestsellers: null,
      lastFetched: null,
      isFetching: false,
      fetchPromise: null
    };
    this.listeners = new Set();
    this.CACHE_DURATION = 10 * 60 * 1000;
  }

  static getInstance() {
    if (!ApiCacheManager.instance) {
      ApiCacheManager.instance = new ApiCacheManager();
    }
    return ApiCacheManager.instance;
  }

  isCacheValid() {
    if (!this.cache.lastFetched) return false;
    return (Date.now() - this.cache.lastFetched) < this.CACHE_DURATION;
  }

  getCache() { return this.cache; }

  setCache(data) {
    this.cache = { ...this.cache, ...data, lastFetched: Date.now() };
    this.notifyListeners();
  }

  setFetching(isFetching, promise = null) {
    this.cache.isFetching = isFetching;
    this.cache.fetchPromise = promise;
    this.notifyListeners();
  }

  addListener(listener) { this.listeners.add(listener); }
  removeListener(listener) { this.listeners.delete(listener); }
  notifyListeners() { this.listeners.forEach(listener => listener(this.cache)); }

  clearCache() {
    this.cache = {
      categories: null, subcategories: null, bestsellers: null,
      lastFetched: null, isFetching: false, fetchPromise: null
    };
    this.notifyListeners();
  }
}
const cacheManager = ApiCacheManager.getInstance();

// ----------------------------------------------------------------------
//  Constants & helpers
// ----------------------------------------------------------------------
const API_BASE_URL = "https://volumaire.backendapihub.com";
const DEFAULT_IMAGE = "/images/dailyroutine2.webp";

const getCompleteImageUrl = (imagePath) => {
  if (!imagePath || imagePath === "/images/product-placeholder.jpg") return DEFAULT_IMAGE;
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) return imagePath;
  if (imagePath.startsWith('//')) return `https:${imagePath}`;
  if (imagePath.startsWith('/uploads') || imagePath.startsWith('/images') || imagePath.startsWith('/storage')) {
    return `${API_BASE_URL}${imagePath}`;
  }
  if (imagePath.includes('uploads/')) return `${API_BASE_URL}/${imagePath}`;
  return `${API_BASE_URL}/uploads/${imagePath}`;
};

// ----------------------------------------------------------------------
//  SVG Icons
// ----------------------------------------------------------------------
const XMarkIcon = ({ className, onClick }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
    onClick={onClick} style={{ cursor: 'pointer' }}>
    <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
      d="M18 6L6 18M6 6l12 12" />
  </svg>
);

const PlusIcon = ({ active }) => (
  <svg className={`svg-icon ${active ? "active" : ""}`} aria-hidden="true" focusable="false" role="presentation"
    xmlns="http://www.w3.org/2000/svg" width="72" height="72" viewBox="0 0 72 72">
    {active ? <path d="M16 36h40" /> : <path d="M36 16v40M16 36h40" />}
  </svg>
);

const MinusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const PlusQtyIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const TrashIcon = ({ className, onClick }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
    onClick={onClick} style={{ cursor: 'pointer' }}>
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </svg>
);

const GiftIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 12 20 22 4 22 4 12" />
    <rect x="2" y="7" width="20" height="5" />
    <line x1="12" y1="22" x2="12" y2="7" />
    <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
    <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
  </svg>
);

const ClearCartIcon = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
    <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </svg>
);

// ----------------------------------------------------------------------
//  Header Component
// ----------------------------------------------------------------------
function Header({ forceSolid = false }) {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    selectedCountry,
    setCurrencyOpen,
    currencyOpen,
    countries,
    formatPrice
  } = useCurrency();

  const cartContext = useCart();
  const cartItems = cartContext?.cartItems || [];
  const cartCount = cartContext?.cartCount || 0;
  const subtotal = cartContext?.subtotal || 0;
  const isCartOpen = cartContext?.isCartOpen || false;
  const openCart = cartContext?.openCart || (() => {});
  const closeCart = cartContext?.closeCart || (() => {});
  const increaseQty = cartContext?.increaseQty || (() => {});
  const decreaseQty = cartContext?.decreaseQty || (() => {});
  const removeFromCart = cartContext?.removeFromCart || (() => {});
  const clearCart = cartContext?.clearCart || (() => {});
  const addToCart = cartContext?.addToCart || (() => {});

  const setCartItems = cartContext?.setCartItems || (() => {});
  const setCartCount = cartContext?.setCartCount || (() => {});
  const setSubtotal = cartContext?.setSubtotal || (() => {});

  const { wishlistCount } = useWishlist();

  const [isSolid, setIsSolid] = useState(false);
  const [hide, setHide] = useState(false);
  const [hideAnnouncement, setHideAnnouncement] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const [bestsellerOpen, setBestsellerOpen] = useState(false);
  const [showUserPopup, setShowUserPopup] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [cartLoading, setCartLoading] = useState(false);
  const [isSyncingCart, setIsSyncingCart] = useState(false);
  const [showClearCartConfirm, setShowClearCartConfirm] = useState(false);
  const [isClearingCart, setIsClearingCart] = useState(false);
  const [showAddToCartMessage, setShowAddToCartMessage] = useState(false);
  const [addToCartMessage, setAddToCartMessage] = useState("");

  const [orderNoteText, setOrderNoteText] = useState("");
  const [showOrderNoteInput, setShowOrderNoteInput] = useState(false);
  const [savedOrderNote, setSavedOrderNote] = useState("");
  const [showSaveMessage, setShowSaveMessage] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");

  const [showAlsoLike, setShowAlsoLike] = useState(false);
  const [alsoLikeProducts, setAlsoLikeProducts] = useState([]);
  const [alsoLikeLoading, setAlsoLikeLoading] = useState(false);
  const carouselRef = useRef(null);
  const carouselScrollRef = useRef(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const cache = cacheManager.getCache();
  const [categories, setCategories] = useState(cache.categories || []);
  const [subcategories, setSubcategories] = useState(cache.subcategories || {});
  const [bestsellerProducts, setBestsellerProducts] = useState(cache.bestsellers || []);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingBestsellers, setLoadingBestsellers] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [drawerMode, setDrawerMode] = useState('main');

  const [isInHeroSection, setIsInHeroSection] = useState(true);
  const [heroLogoInNavbar, setHeroLogoInNavbar] = useState(false);

  const componentMounted = useRef(false);
  const hasFetchedInitialData = useRef(false);
  const cartFetchInProgress = useRef(false);
  const lastCartFetchTime = useRef(0);
  const CART_FETCH_COOLDOWN = 2000;
  const cartSyncPerformed = useRef(false);
  const hasInitialCartFetch = useRef(false);
  const cartFetchAttempted = useRef(false);
  const saveMessageTimeout = useRef(null);
  const addToCartMessageTimeout = useRef(null);
  const scrollRAF = useRef(null);

  const lastScrollY = useRef(0);
  const hideRef = useRef(false);
  const hideAnnouncementRef = useRef(false);
  const isInHeroSectionRef = useRef(true);
  const locationPathnameRef = useRef(location.pathname);

  useEffect(() => {
    locationPathnameRef.current = location.pathname;
  }, [location.pathname]);

  // ------------------------------------------------------------------
  // Carousel helpers
  // ------------------------------------------------------------------
  const checkCarouselScroll = useCallback(() => {
    const el = carouselRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 2);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 2);
  }, []);

  const scrollCarouselLeft = useCallback(() => {
    const el = carouselRef.current;
    if (!el) return;
    const card = el.querySelector('.also-like-product-card');
    const scrollAmount = card ? card.offsetWidth + 16 : 300;
    el.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    setTimeout(checkCarouselScroll, 380);
  }, [checkCarouselScroll]);

  const scrollCarouselRight = useCallback(() => {
    const el = carouselRef.current;
    if (!el) return;
    const card = el.querySelector('.also-like-product-card');
    const scrollAmount = card ? card.offsetWidth + 16 : 300;
    el.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    setTimeout(checkCarouselScroll, 380);
  }, [checkCarouselScroll]);

  const handleAlsoLikeToggle = useCallback(() => {
    setShowAlsoLike(prev => {
      if (prev && carouselRef.current) {
        carouselScrollRef.current = carouselRef.current.scrollLeft;
      }
      return !prev;
    });
  }, []);

  useEffect(() => {
    if (showAlsoLike) {
      requestAnimationFrame(() => {
        if (carouselRef.current) {
          carouselRef.current.scrollLeft = carouselScrollRef.current;
          checkCarouselScroll();
        }
      });
    }
  }, [showAlsoLike, checkCarouselScroll]);

  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;
    carousel.addEventListener('scroll', checkCarouselScroll, { passive: true });
    return () => carousel.removeEventListener('scroll', checkCarouselScroll);
  }, [checkCarouselScroll]);

  useEffect(() => {
    window.addEventListener('resize', checkCarouselScroll);
    return () => window.removeEventListener('resize', checkCarouselScroll);
  }, [checkCarouselScroll]);

  // ------------------------------------------------------------------
  // Price extraction
  // ------------------------------------------------------------------
  const extractProductPrice = useCallback((product) => {
    const fields = [
      'price', 'salePrice', 'sale_price', 'priceValue',
      'finalPrice', 'regular_price', 'regularPrice',
      'base_price', 'basePrice', 'mrp', 'cost', 'amount'
    ];
    for (const field of fields) {
      const val = product[field];
      if (val !== undefined && val !== null && val !== '') {
        const parsed = typeof val === 'number' ? val : parseFloat(String(val).replace(/[^\d.-]/g, ''));
        if (!isNaN(parsed) && parsed > 0) return parsed;
      }
    }
    if (product.pricing) {
      const p = product.pricing;
      const v = p.sale || p.regular || p.base || p.amount || p.value;
      if (v) {
        const parsed = typeof v === 'number' ? v : parseFloat(String(v).replace(/[^\d.-]/g, ''));
        if (!isNaN(parsed) && parsed > 0) return parsed;
      }
    }
    return 0;
  }, []);

  const getItemPrice = useCallback((item) => {
    if (item.basePrice !== undefined && item.basePrice !== null) return item.basePrice;
    if (item.price !== undefined && item.price !== null) {
      if (typeof item.price === 'number') return item.price;
      if (typeof item.price === 'string') {
        const cleaned = item.price.replace(',', '.').replace(/[^\d.-]/g, '');
        const parsed = parseFloat(cleaned);
        return isNaN(parsed) ? 0 : parsed;
      }
    }
    if (item.priceValue !== undefined) return item.priceValue;
    if (item.originalPrice !== undefined) return item.originalPrice;
    if (item.finalPrice !== undefined) return item.finalPrice;
    if (item.product?.price) return parseFloat(item.product.price) || 0;
    return 0;
  }, []);

  // ------------------------------------------------------------------
  // ✅ FIXED: Enhanced add to cart from also like products with proper state update
  // ------------------------------------------------------------------
  const handleAddToCartFromAlsoLike = useCallback((product, e) => {
    e.stopPropagation();
    
    console.log("Adding product to cart:", product);
    
    // Create a properly formatted product object for the cart
    const productPrice = product.price || product.priceValue || 0;
    
    const productToAdd = {
      id: product.id,
      productId: product.id,
      name: product.name,
      title: product.name,
      price: productPrice,
      priceValue: productPrice,
      basePrice: productPrice,
      image: product.image || product.img,
      img: product.image || product.img,
      qty: 1,
      quantity: 1,
      selectedSize: product.selectedSize || null,
      selectedFragrance: product.selectedFragrance || null
    };
    
    console.log("Formatted product for cart:", productToAdd);
    
    // Add to cart using the context function
    if (addToCart) {
      addToCart(productToAdd, 1);
      
      // Show success message
      setAddToCartMessage(`${product.name} added to cart!`);
      setShowAddToCartMessage(true);
      
      if (addToCartMessageTimeout.current) {
        clearTimeout(addToCartMessageTimeout.current);
      }
      addToCartMessageTimeout.current = setTimeout(() => {
        setShowAddToCartMessage(false);
        setAddToCartMessage("");
      }, 2000);
      
      // Force cart drawer to show if needed
      if (!isCartOpen) {
        setTimeout(() => {
          openCart();
        }, 100);
      }
    } else if (cartContext?.addItem) {
      cartContext.addItem(productToAdd);
    } else {
      console.error("addToCart function is not available");
      setAddToCartMessage("Failed to add item to cart");
      setShowAddToCartMessage(true);
      setTimeout(() => {
        setShowAddToCartMessage(false);
        setAddToCartMessage("");
      }, 2000);
    }
  }, [addToCart, cartContext, isCartOpen, openCart]);

  // ------------------------------------------------------------------
  // Fetch "You might also like" products
  // ------------------------------------------------------------------
  useEffect(() => {
    const fetchAlsoLikeProducts = async () => {
      if (alsoLikeProducts.length > 0) return;
      setAlsoLikeLoading(true);
      try {
        const { getBestsellerProducts } = await import("../pages/api");
        const response = await getBestsellerProducts();

        let productsData = [];
        if (Array.isArray(response)) productsData = response;
        else if (Array.isArray(response?.data)) productsData = response.data;
        else if (Array.isArray(response?.products)) productsData = response.products;
        else if (Array.isArray(response?.result)) productsData = response.result;
        else if (Array.isArray(response?.bestsellers)) productsData = response.bestsellers;
        else if (Array.isArray(response?.items)) productsData = response.items;

        const mappedProducts = productsData.slice(0, 10).map(product => {
          let productImage = "";
          if (product.images?.length > 0) {
            const img = product.images[0];
            productImage = typeof img === 'string' ? img : (img?.url || img?.src || img?.path || "");
          } else if (product.image) {
            productImage = typeof product.image === 'string' ? product.image : (product.image?.url || product.image?.src || "");
          } else if (product.img) {
            productImage = product.img;
          } else if (product.img1) {
            productImage = product.img1;
          } else if (product.thumbnail) {
            productImage = product.thumbnail;
          } else if (product.featured_image) {
            productImage = product.featured_image;
          } else if (product.imageUrl) {
            productImage = product.imageUrl;
          }

          const productPrice = extractProductPrice(product);

          return {
            id: product.id || product._id || product.productId || `product-${Math.random()}`,
            name: product.name || product.title || product.productName || "Product",
            brand: product.brand || product.vendor || product.brandName || product.category?.name || "",
            price: productPrice,
            priceValue: productPrice,
            image: productImage,
            img: productImage,
            slug: product.slug || product.handle || (product.name || '').toLowerCase().replace(/\s+/g, '-')
          };
        });

        setAlsoLikeProducts(mappedProducts);
        setTimeout(checkCarouselScroll, 250);
      } catch (error) {
        console.error("Error fetching also like products:", error);
        setAlsoLikeProducts([
          { id: 1, name: "Bakuchiol Retinol Alternative Serum", brand: "HERBIVORE", price: 5300, priceValue: 5300, image: "/images/bestsellersimage1.webp", img: "/images/bestsellersimage1.webp" },
          { id: 2, name: "Vitamin C Brightening Serum", brand: "VOLUMAIRE", price: 4800, priceValue: 4800, image: "/images/bestsellersimage2.webp", img: "/images/bestsellersimage2.webp" },
          { id: 3, name: "Hyaluronic Acid Hydrating Serum", brand: "VOLUMAIRE", price: 5200, priceValue: 5200, image: "/images/bestsellersimage3.webp", img: "/images/bestsellersimage3.webp" },
          { id: 4, name: "Niacinamide B3 Serum", brand: "VOLUMAIRE", price: 4500, priceValue: 4500, image: "/images/bestsellersimage4.webp", img: "/images/bestsellersimage4.webp" },
          { id: 5, name: "Retinol Renewal Night Cream", brand: "VOLUMAIRE", price: 6200, priceValue: 6200, image: "/images/bestsellersimage1.webp", img: "/images/bestsellersimage1.webp" },
          { id: 6, name: "Peptide Complex Moisturizer", brand: "VOLUMAIRE", price: 5800, priceValue: 5800, image: "/images/bestsellersimage2.webp", img: "/images/bestsellersimage2.webp" },
          { id: 7, name: "AHA BHA Exfoliating Solution", brand: "VOLUMAIRE", price: 4900, priceValue: 4900, image: "/images/bestsellersimage3.webp", img: "/images/bestsellersimage3.webp" },
          { id: 8, name: "Ceramide Barrier Repair Cream", brand: "VOLUMAIRE", price: 5500, priceValue: 5500, image: "/images/bestsellersimage4.webp", img: "/images/bestsellersimage4.webp" },
          { id: 9, name: "Collagen Boosting Serum", brand: "VOLUMAIRE", price: 6000, priceValue: 6000, image: "/images/bestsellersimage1.webp", img: "/images/bestsellersimage1.webp" },
          { id: 10, name: "Antioxidant Face Oil", brand: "VOLUMAIRE", price: 5700, priceValue: 5700, image: "/images/bestsellersimage2.webp", img: "/images/bestsellersimage2.webp" }
        ]);
        setTimeout(checkCarouselScroll, 250);
      } finally {
        setAlsoLikeLoading(false);
      }
    };

    fetchAlsoLikeProducts();
  }, [alsoLikeProducts.length, checkCarouselScroll, extractProductPrice]);

  useEffect(() => {
    if (location.pathname !== "/") return;
    const observer = new MutationObserver(() => {
      const inNavbar = document.body.classList.contains("hero-logo-in-navbar");
      setHeroLogoInNavbar(inNavbar);
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    setHeroLogoInNavbar(document.body.classList.contains("hero-logo-in-navbar"));
    return () => observer.disconnect();
  }, [location.pathname]);

  const isProductPage = location.pathname.includes("/product/");
  const isProductListPage = location.pathname === "/products" || location.pathname.includes("/products?");
  const isLoginPage = location.pathname === "/login";
  const isWishlistPage = location.pathname === "/wishlist";
  const isPolicyPage =
    location.pathname.includes("/shipping-policy") ||
    location.pathname.includes("/return-refund") ||
    location.pathname.includes("/legal") ||
    location.pathname.includes("/privacy-policy") ||
    location.pathname.includes("/terms-of-service") ||
    location.pathname.includes("/cookie-policy");
  const isSpecialPage =
    location.pathname.includes("/account/orders") ||
    location.pathname.includes("/account/profile") ||
    location.pathname === "/wishlist" ||
    location.pathname.includes("/wishlist") ||
    location.pathname === "/contact" ||
    location.pathname === "/checkout" ||
    location.pathname.includes("/checkout") ||
    location.pathname === "/search" ||
    location.pathname.includes("/search/") ||
    isProductListPage ||
    isPolicyPage;

  const handleAnnouncementClick = () => { navigate("/shipping-policy"); };

  useEffect(() => {
    const handleCacheUpdate = (newCache) => {
      if (!componentMounted.current) return;
      if (newCache.categories && !arraysEqual(newCache.categories, categories)) setCategories(newCache.categories);
      if (newCache.subcategories && !objectsEqual(newCache.subcategories, subcategories)) setSubcategories(newCache.subcategories);
      if (newCache.bestsellers && !arraysEqual(newCache.bestsellers, bestsellerProducts)) setBestsellerProducts(newCache.bestsellers);
    };
    cacheManager.addListener(handleCacheUpdate);
    return () => cacheManager.removeListener(handleCacheUpdate);
  }, [categories, subcategories, bestsellerProducts]);

  const arraysEqual = (a, b) => {
    if (!a || !b) return false;
    if (a.length !== b.length) return false;
    return a.every((item, index) => JSON.stringify(item) === JSON.stringify(b[index]));
  };
  const objectsEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

  const processCartDataAndUpdateContext = useCallback((cartData) => {
    let items = [];
    if (cartData.data?.items && Array.isArray(cartData.data.items)) items = cartData.data.items;
    else if (cartData.items && Array.isArray(cartData.items)) items = cartData.items;
    else if (Array.isArray(cartData)) items = cartData;

    if (items.length > 0) {
      const transformedItems = items.map(item => {
        let selectedSize, selectedFragrance;
        if (item.selectedAttributes) {
          try {
            let attrs = item.selectedAttributes;
            if (typeof attrs === 'string') {
              try { attrs = JSON.parse(attrs); } catch { }
            }
            if (Array.isArray(attrs)) {
              if (attrs.length >= 2) {
                if (typeof attrs[0] === 'object' && attrs[0] !== null) {
                  selectedSize = attrs[0]?.value || attrs[0]?.toString() || '';
                  selectedFragrance = attrs[1]?.value || attrs[1]?.toString() || '';
                } else {
                  selectedSize = attrs[0]?.toString() || '';
                  selectedFragrance = attrs[1]?.toString() || '';
                }
              } else if (attrs.length === 1) {
                if (typeof attrs[0] === 'object' && attrs[0] !== null) {
                  selectedSize = attrs[0]?.value || attrs[0]?.toString() || '';
                } else {
                  selectedSize = attrs[0]?.toString() || '';
                }
              }
            } else if (typeof attrs === 'object' && attrs !== null) {
              selectedSize = attrs.size || attrs.Size || attrs.SIZE || '';
              selectedFragrance = attrs.fragrance || attrs.Fragrance || attrs.FRAGRANCE || attrs.color || attrs.Color || '';
            } else if (typeof attrs === 'number' || typeof attrs === 'string') {
              selectedSize = attrs.toString();
            }
          } catch (e) { }
        }

        let image;
        if (item.selectedAttributeDetails?.[0]?.images?.[0]) image = item.selectedAttributeDetails[0].images[0];
        if (!image && item.product) {
          if (item.product.images?.[0]) image = item.product.images[0];
          else if (item.product.image) image = item.product.image;
          else if (item.product.img) image = item.product.img;
          else if (item.product.imageUrl) image = item.product.imageUrl;
          else if (item.product.image_url) image = item.product.image_url;
        }
        if (!image) {
          if (item.image) image = item.image;
          else if (item.img) image = item.img;
          else if (item.img1) image = item.img1;
        }

        const productName = item.product?.name || item.name || item.title || "Product";
        const basePrice = getItemPrice(item);
        const productId = item.productId || item.product?.id || item.id;

        return {
          uniqueId: item.id ? item.id.toString() : `server-${Date.now()}-${Math.random()}`,
          id: productId,
          cartItemId: item.id,
          productId: productId,
          name: productName,
          title: productName,
          price: basePrice,
          priceValue: basePrice,
          qty: item.quantity || item.qty || 1,
          ...(selectedSize && { selectedSize }),
          ...(selectedFragrance && { selectedFragrance }),
          ...(image && { image }),
          ...(image && { img: image }),
          ...(image && { img1: image }),
          finalPrice: item.finalPrice,
          totalPrice: item.totalPrice,
          product: item.product,
          selectedAttributeDetails: item.selectedAttributeDetails,
          basePrice: basePrice,
          baseCurrency: 'USD'
        };
      });

      setCartItems(transformedItems);
      const count = transformedItems.reduce((total, item) => total + (item.qty || 1), 0);
      const total = transformedItems.reduce((sum, item) => {
        const price = getItemPrice(item);
        return sum + (price * (item.qty || 1));
      }, 0);
      setCartCount(count);
      setSubtotal(total);
    } else {
      setCartItems([]);
      setCartCount(0);
      setSubtotal(0);
    }
  }, [setCartItems, setCartCount, setSubtotal, getItemPrice]);

  const fetchCartFromAPI = useCallback(async (forceRefresh = false) => {
    const token = localStorage.getItem("token");
    const loggedIn = localStorage.getItem("isLoggedIn") === "true";
    if (!token || !loggedIn) return;

    if (cartFetchInProgress.current) return;

    const now = Date.now();
    if (!forceRefresh && (now - lastCartFetchTime.current < CART_FETCH_COOLDOWN)) return;

    cartFetchInProgress.current = true;
    lastCartFetchTime.current = now;
    setCartLoading(true);

    try {
      const cartData = await getCartAPI({ skipCache: true, useCache: false });
      if (componentMounted.current) {
        processCartDataAndUpdateContext(cartData);
        hasInitialCartFetch.current = true;
        cartFetchAttempted.current = true;
      }
    } catch (error) {
      console.error("Error fetching cart from API:", error);
    } finally {
      if (componentMounted.current) {
        setCartLoading(false);
      }
      setTimeout(() => { cartFetchInProgress.current = false; }, 100);
    }
  }, [processCartDataAndUpdateContext]);

  const syncLocalCartWithServer = useCallback(async (localItems) => {
    const token = localStorage.getItem("token");
    const loggedIn = localStorage.getItem("isLoggedIn") === "true";
    if (!token || !loggedIn || !localItems?.length) return;
    if (cartSyncPerformed.current) return;

    try {
      setIsSyncingCart(true);
      cartSyncPerformed.current = true;
      const updatedCartData = await syncCartAPI(localItems);
      if (updatedCartData && componentMounted.current) processCartDataAndUpdateContext(updatedCartData);
      localStorage.removeItem('cartItems');
    } catch (syncError) {
      console.error("Error syncing cart:", syncError);
      cartSyncPerformed.current = false;
      try { await fetchCartFromAPI(true); } catch (fetchError) { }
    } finally {
      if (componentMounted.current) setIsSyncingCart(false);
    }
  }, [processCartDataAndUpdateContext, fetchCartFromAPI]);

  const fetchAllData = useCallback(async () => {
    if (cacheManager.isCacheValid() && cache.categories && cache.subcategories && cache.bestsellers) return;
    if (cache.isFetching && cache.fetchPromise) {
      try { await cache.fetchPromise; } catch (error) { }
      return;
    }

    setLoadingCategories(true);
    setLoadingBestsellers(true);

    const fetchPromise = (async () => {
      try {
        const { getCategories, getSubCategories, getBestsellerProducts } = await import("../pages/api");
        const [categoriesResponse, subcategoriesResponse, bestsellerResponse] = await Promise.all([
          getCategories(),
          getSubCategories(),
          getBestsellerProducts()
        ]);

        let categoriesData = [];
        if (Array.isArray(categoriesResponse)) categoriesData = categoriesResponse;
        else if (categoriesResponse?.data && Array.isArray(categoriesResponse.data)) categoriesData = categoriesResponse.data;
        else if (categoriesResponse?.categories && Array.isArray(categoriesResponse.categories)) categoriesData = categoriesResponse.categories;

        let subcategoriesData = [];
        if (Array.isArray(subcategoriesResponse)) subcategoriesData = subcategoriesResponse;
        else if (subcategoriesResponse?.data && Array.isArray(subcategoriesResponse.data)) subcategoriesData = subcategoriesResponse.data;
        else if (subcategoriesResponse?.subcategories && Array.isArray(subcategoriesResponse.subcategories)) subcategoriesData = subcategoriesResponse.subcategories;

        let bestsellerData = [];
        if (Array.isArray(bestsellerResponse)) bestsellerData = bestsellerResponse;
        else if (bestsellerResponse?.data && Array.isArray(bestsellerResponse.data)) bestsellerData = bestsellerResponse.data;
        else if (bestsellerResponse?.products && Array.isArray(bestsellerResponse.products)) bestsellerData = bestsellerResponse.products;
        else if (bestsellerResponse?.result && Array.isArray(bestsellerResponse.result)) bestsellerData = bestsellerResponse.result;
        else if (bestsellerResponse?.bestsellers && Array.isArray(bestsellerResponse.bestsellers)) bestsellerData = bestsellerResponse.bestsellers;

        const processedCategories = categoriesData.map((category, index) => ({
          id: category.id || category._id || `category-${index}`,
          originalId: category.id || category._id || `category-${index}`,
          name: category.name || category.categoryName || category.title || `Category ${index + 1}`,
          slug: category.slug || (category.name || '').toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
          description: category.description || category.name || '',
          isMainCategory: true
        })).slice(0, 6);

        const processedSubcategories = {};
        processedCategories.forEach(cat => { processedSubcategories[cat.id] = []; });

        subcategoriesData.forEach((subcat, index) => {
          const parentId = subcat.categoryId || subcat.parentId || subcat.parentCategoryId;
          if (parentId) {
            const parent = processedCategories.find(cat => cat.originalId === parentId || cat.id === parentId);
            if (parent) {
              processedSubcategories[parent.id].push({
                id: subcat.id || subcat._id || `subcategory-${index}`,
                name: subcat.name || subcat.subcategoryName || subcat.title || `Subcategory ${index + 1}`,
                slug: subcat.slug || (subcat.name || '').toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
                originalId: subcat.id || subcat._id || `subcategory-${index}`,
                parentId,
                isSubcategory: true
              });
            }
          }
        });

        const processedBestsellers = bestsellerData.slice(0, 10).map((product, index) => {
          let productImage;
          if (product.images && product.images.length > 0) productImage = product.images[0];
          else if (product.image) productImage = product.image;
          else if (product.img) productImage = product.img;
          else if (product.img1) productImage = product.img1;
          else if (product.imageUrl) productImage = product.imageUrl;
          else if (product.featured_image) productImage = product.featured_image;

          let productPrice = 0;
          if (product.price) productPrice = typeof product.price === 'number' ? product.price : parseFloat(product.price) || 0;
          else if (product.regular_price) productPrice = typeof product.regular_price === 'number' ? product.regular_price : parseFloat(product.regular_price) || 0;
          else if (product.sale_price) productPrice = typeof product.sale_price === 'number' ? product.sale_price : parseFloat(product.sale_price) || 0;

          let productSlug = product.slug || product.handle || '';
          if (!productSlug && product.name) productSlug = product.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

          return {
            id: product.id || product._id || product.productId || `bestseller-${index}`,
            name: product.name || product.title || product.productName || `Bestseller ${index + 1}`,
            price: productPrice,
            priceValue: productPrice,
            originalPrice: product.regular_price || product.compare_at_price || productPrice,
            ...(productImage && { img: productImage }),
            ...(productImage && { image: productImage }),
            slug: productSlug,
            description: product.description || product.short_description || '',
            category: product.category,
            sku: product.sku,
            inStock: product.in_stock !== undefined ? product.in_stock : true
          };
        });

        cacheManager.setCache({
          categories: processedCategories,
          subcategories: processedSubcategories,
          bestsellers: processedBestsellers
        });

      } catch (error) {
        console.error("Error fetching categories/bestsellers:", error);
        cacheManager.setCache({ categories: [], subcategories: {}, bestsellers: [] });
      } finally {
        cacheManager.setFetching(false, null);
        if (componentMounted.current) {
          setLoadingCategories(false);
          setLoadingBestsellers(false);
        }
      }
    })();

    cacheManager.setFetching(true, fetchPromise);
    await fetchPromise;
  }, []);

  const handleCategoryExpand = (categoryId) => {
    setExpandedCategory(prev => prev === categoryId ? null : categoryId);
  };

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    setDrawerMode('category');
    setDrawerOpen(true);
    setShopOpen(false);
    setBestsellerOpen(false);
    setExpandedCategory(null);
  };

  const handleBackToMain = () => {
    setDrawerMode('main');
    setSelectedCategory(null);
    setShopOpen(false);
    setBestsellerOpen(false);
    setExpandedCategory(null);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setDrawerMode('main');
    setSelectedCategory(null);
    setShopOpen(false);
    setBestsellerOpen(false);
    setExpandedCategory(null);
  };

  const handleBestsellerClick = () => {
    setDrawerMode('main');
    setBestsellerOpen(true);
    setShopOpen(false);
    setExpandedCategory(null);
    setDrawerOpen(true);
  };

  const handleViewAllBestsellers = () => {
    navigate("/products");
    handleDrawerClose();
  };

  const handleBestsellerProductClick = (product) => {
    if (product.id) navigate(`/product/${product.id}`);
    else console.error("Product has no ID:", product);
    handleDrawerClose();
  };

  useEffect(() => {
    const checkLoginStatus = () => {
      const token = localStorage.getItem("token");
      const loggedIn = localStorage.getItem("isLoggedIn") === "true";
      if (token && loggedIn) {
        setIsLoggedIn(true);
        setUserProfile({
          name: localStorage.getItem("userName") || "User",
          email: localStorage.getItem("userEmail") || "user@example.com"
        });
      } else {
        setIsLoggedIn(false);
        setUserProfile(null);
        cartSyncPerformed.current = false;
      }
    };
    checkLoginStatus();
    if (isWishlistPage && !localStorage.getItem("isLoggedIn")) navigate("/login");
  }, [location.pathname, navigate, isWishlistPage]);

  useEffect(() => {
    componentMounted.current = true;
    if (!hasFetchedInitialData.current && (!cacheManager.isCacheValid() || !cache.categories)) {
      hasFetchedInitialData.current = true;
      fetchAllData();
    }
    return () => { componentMounted.current = false; };
  }, [fetchAllData, cache.categories]);

  useEffect(() => {
    if (isLoggedIn) {
      fetchCartFromAPI(true);
    }
  }, [isLoggedIn, fetchCartFromAPI]);

  useEffect(() => {
    if (isCartOpen && isLoggedIn) {
      fetchCartFromAPI(true);
      if (!cartSyncPerformed.current) {
        const localCart = localStorage.getItem('cartItems');
        if (localCart) {
          try {
            const localItems = JSON.parse(localCart);
            if (localItems.length > 0) {
              const timeout = setTimeout(() => syncLocalCartWithServer(localItems), 800);
              return () => clearTimeout(timeout);
            }
          } catch (error) { }
        }
      }
    }
  }, [isCartOpen, isLoggedIn, fetchCartFromAPI, syncLocalCartWithServer]);

  useEffect(() => {
    const handleScroll = () => {
      if (scrollRAF.current) cancelAnimationFrame(scrollRAF.current);
      scrollRAF.current = requestAnimationFrame(() => {
        const currentScroll = window.scrollY;
        const previousScroll = lastScrollY.current;
        const pathname = locationPathnameRef.current;

        const heroElement = document.querySelector('.home-hero-one');
        let currentlyInHero = false;
        if (heroElement) {
          const heroRect = heroElement.getBoundingClientRect();
          currentlyInHero = heroRect.bottom > 80;
        }

        if (currentlyInHero !== isInHeroSectionRef.current) {
          isInHeroSectionRef.current = currentlyInHero;
          setIsInHeroSection(currentlyInHero);
        }

        const shouldHideAnnouncement = currentScroll > 10;
        if (shouldHideAnnouncement !== hideAnnouncementRef.current) {
          hideAnnouncementRef.current = shouldHideAnnouncement;
          setHideAnnouncement(shouldHideAnnouncement);
        }

        if (pathname === "/" && currentlyInHero) {
          if (hideRef.current !== false) { hideRef.current = false; setHide(false); }
          lastScrollY.current = currentScroll;
          scrollRAF.current = null;
          return;
        }

        if (currentScroll <= 80) {
          if (hideRef.current !== false) { hideRef.current = false; setHide(false); }
        } else if (currentScroll > previousScroll) {
          if (hideRef.current !== true) { hideRef.current = true; setHide(true); }
        } else if (currentScroll < previousScroll) {
          if (hideRef.current !== false) { hideRef.current = false; setHide(false); }
        }

        lastScrollY.current = currentScroll;
        scrollRAF.current = null;
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollRAF.current) cancelAnimationFrame(scrollRAF.current);
    };
  }, []);

  useEffect(() => {
    let newIsSolid = false;
    if (forceSolid) newIsSolid = true;
    else if (isProductPage || isProductListPage || isLoginPage || isSpecialPage) newIsSolid = true;
    else if (location.pathname === "/") newIsSolid = !isInHeroSection || document.body.classList.contains("hero-scrolled-solid");
    else newIsSolid = true;
    if (newIsSolid !== isSolid) setIsSolid(newIsSolid);
  }, [forceSolid, isProductPage, isProductListPage, isLoginPage, isSpecialPage, isInHeroSection, location.pathname, isSolid, heroLogoInNavbar]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const popup = document.querySelector('.user-hover-wrapper');
      if (showUserPopup && popup && !popup.contains(e.target)) setShowUserPopup(false);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showUserPopup]);

  useEffect(() => {
    return () => { 
      if (saveMessageTimeout.current) clearTimeout(saveMessageTimeout.current);
      if (addToCartMessageTimeout.current) clearTimeout(addToCartMessageTimeout.current);
    };
  }, []);

  const handleHomeClick = () => { navigate("/"); handleDrawerClose(); };
  const handleUserIconClick = () => isLoggedIn ? setShowUserPopup(!showUserPopup) : navigate("/login");
  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("isLoggedIn");
    localStorage.removeItem("userName");
    localStorage.removeItem("userEmail");
    setIsLoggedIn(false);
    setUserProfile(null);
    setShowUserPopup(false);
    cartSyncPerformed.current = false;
    cartFetchAttempted.current = false;
    clearCart();
    navigate("/");
  };

  const handleCurrencyClick = () => setCurrencyOpen(true);

  const handleAlsoLikeProductClick = (product) => {
    if (product.id) { navigate(`/product/${product.id}`); closeCart(); }
  };

  const handleCartItemImageClick = (item) => {
    const productId = item.productId || item.id;
    if (productId) { navigate(`/product/${productId}`); closeCart(); }
  };

  const handleIncreaseQty = async (uniqueId) => {
    const token = localStorage.getItem("token");
    const loggedIn = localStorage.getItem("isLoggedIn") === "true";
    const item = cartItems.find(i => i.uniqueId === uniqueId);
    if (!item) return;
    increaseQty(uniqueId);
    if (token && loggedIn && item.cartItemId) {
      try {
        await updateCartItemAPI(item.cartItemId, (item.qty || 1) + 1);
        fetchCartFromAPI(true);
      } catch (error) {
        console.error("Error updating quantity:", error);
        decreaseQty(uniqueId);
      }
    }
  };

  const handleDecreaseQty = async (uniqueId) => {
    const token = localStorage.getItem("token");
    const loggedIn = localStorage.getItem("isLoggedIn") === "true";
    const item = cartItems.find(i => i.uniqueId === uniqueId);
    if (!item) return;
    const newQty = (item.qty || 1) - 1;
    if (newQty < 1) { handleRemoveFromCart(uniqueId); return; }
    decreaseQty(uniqueId);
    if (token && loggedIn && item.cartItemId) {
      try {
        await updateCartItemAPI(item.cartItemId, newQty);
        fetchCartFromAPI(true);
      } catch (error) {
        console.error("Error updating quantity:", error);
        increaseQty(uniqueId);
      }
    }
  };

  const handleRemoveFromCart = async (uniqueId) => {
    const token = localStorage.getItem("token");
    const loggedIn = localStorage.getItem("isLoggedIn") === "true";
    const item = cartItems.find(i => i.uniqueId === uniqueId);
    removeFromCart(uniqueId);
    if (token && loggedIn && item?.cartItemId) {
      try {
        await removeCartItemAPI(item.cartItemId);
        fetchCartFromAPI(true);
      } catch (error) { console.error("Error removing item:", error); }
    }
  };

  const handleClearCartDrawer = () => setShowClearCartConfirm(true);

  const confirmClearCart = async () => {
    setIsClearingCart(true);
    const token = localStorage.getItem("token");
    const loggedIn = localStorage.getItem("isLoggedIn") === "true";
    try {
      clearCart();
      localStorage.removeItem('cartItems');
      cartSyncPerformed.current = false;
      cartFetchAttempted.current = false;
      if (token && loggedIn) { await clearCartAPI(); fetchCartFromAPI(true); }
    } catch (error) { console.error("Error clearing cart:", error); }
    finally { setIsClearingCart(false); setShowClearCartConfirm(false); }
  };

  const cancelClearCart = () => setShowClearCartConfirm(false);
  const handleCheckout = () => { closeCart(); navigate("/checkout"); };
  const handleOrderLinkClick = (e) => { e.preventDefault(); setShowUserPopup(false); navigate("/account/orders"); };

  const handleAddOrderNoteClick = (e) => {
    e.preventDefault();
    setShowOrderNoteInput(prev => !prev);
    if (!showOrderNoteInput && savedOrderNote) setOrderNoteText(savedOrderNote);
  };

  const handleOrderNoteChange = (e) => { setOrderNoteText(e.target.value); };

  const handleSaveOrderNote = () => {
    if (orderNoteText.trim()) {
      setSavedOrderNote(orderNoteText);
      setSaveMessage("Order note saved!");
      setShowSaveMessage(true);
      localStorage.setItem("orderNote", orderNoteText);
      setShowOrderNoteInput(false);
      if (saveMessageTimeout.current) clearTimeout(saveMessageTimeout.current);
      saveMessageTimeout.current = setTimeout(() => { setShowSaveMessage(false); setSaveMessage(""); }, 2000);
    } else {
      setSaveMessage("Please enter a note before saving");
      setShowSaveMessage(true);
      saveMessageTimeout.current = setTimeout(() => { setShowSaveMessage(false); setSaveMessage(""); }, 2000);
    }
  };

  const calculateShippingProgress = () => {
    const freeShippingThreshold = 52.05;
    const remaining = Math.max(0, freeShippingThreshold - subtotal);
    const progress = Math.min(100, (subtotal / freeShippingThreshold) * 100);
    return { remaining: remaining.toFixed(2).replace(',', '.'), progress };
  };
  const shippingProgress = calculateShippingProgress();

  const isHomePage = location.pathname === "/";
  const shouldBeTransparent = !forceSolid &&
    isHomePage && isInHeroSection && !heroLogoInNavbar &&
    !isProductPage && !isProductListPage && !isLoginPage && !isSpecialPage;

  const showNavbarLogo = heroLogoInNavbar || !isHomePage || isProductPage || isProductListPage || isLoginPage || isSpecialPage || !isInHeroSection;

  const renderWishlistIcon = () => isLoggedIn ? (
    <Link to="/wishlist" className="wishlist-icon-wrapper">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="icon">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
      </svg>
      {wishlistCount > 0 && <span className="wishlist-badge">{wishlistCount}</span>}
    </Link>
  ) : null;

  const renderDrawerWishlistLink = () => isLoggedIn ? (
    <Link to="/wishlist" className="drawer-bottom-link" onClick={handleDrawerClose}>
      <span>Wishlist {wishlistCount > 0 && `(${wishlistCount})`}</span>
    </Link>
  ) : null;

  const getFlagImageUrl = (country) => {
    if (!country) return "/images/flags/default.svg";
    const flagField = country.flag || country.flagUrl || country.image || country.countryFlag;
    if (flagField && typeof flagField === 'string' && flagField.trim() !== '') return flagField;
    if (country.countryCode) return `https://flagcdn.com/w320/${country.countryCode.toLowerCase()}.png`;
    return "/images/flags/default.svg";
  };

  const renderAlsoLikePrice = (product) => {
    const price = product.price || product.priceValue || 0;
    if (!price || price <= 0) return null;
    try {
      const formatted = formatPrice(price);
      return <p className="also-like-product-price">{formatted}</p>;
    } catch {
      return <p className="also-like-product-price">₹{Number(price).toLocaleString('en-IN')}</p>;
    }
  };

  return (
    <>
      <header
        className={`header ${shouldBeTransparent ? "transparent" : "solid"} ${hide ? "hide" : "show"}`}
        style={drawerOpen ? { display: 'none' } : {}}
      >
        {!hideAnnouncement && (
          <div className="announcement-bar">
            <div className="announcement-marquee" onClick={handleAnnouncementClick} style={{ cursor: 'pointer' }}>
              <span>Free Shipping Over Rs.50! Returns are always on us. › </span>
              <span>Free Shipping Over Rs.50! Returns are always on us. › </span>
              <span>Free Shipping Over Rs.50! Returns are always on us. › </span>
              <span>Free Shipping Over Rs.50! Returns are always on us. › </span>
            </div>
          </div>
        )}
        <nav className="navbar">
          <div className="navbar-left">
            <FiMenu className="menu-icon" onClick={() => { setDrawerMode('main'); setDrawerOpen(true); }} />
            <ul className="nav-links">
              <li onClick={handleHomeClick}>Home</li>
              {categories.map(category => (
                <li key={category.id} onClick={() => handleCategoryClick(category)}>{category.name}</li>
              ))}
              <li onClick={handleBestsellerClick}>Bestseller</li>
              <li onClick={() => { navigate("/products"); handleDrawerClose(); }}>Sale</li>
            </ul>
          </div>

          <div className={`navbar-center ${showNavbarLogo ? 'visible' : ''}`}>
            <span className="navbar-volumair-logo">VOLUMAIRE</span>
          </div>

          <div className="navbar-right">
            <div className="currency-display" onClick={handleCurrencyClick}>
              <img
                src={getFlagImageUrl(selectedCountry)}
                alt={selectedCountry?.countryName || selectedCountry?.countryCode || 'Currency'}
                onError={(e) => { e.target.onerror = null; e.target.src = "/images/flags/default.svg"; }}
              />
              <span>{selectedCountry?.currency?.symbol || selectedCountry?.currency?.code}</span>
              <FaChevronDown className="currency-arrow" />
            </div>
            <Link to="/search"><BsSearch className="icon" /></Link>
            {renderWishlistIcon()}
            <div className="user-hover-wrapper">
              <BsPersonCircle className="icon" onClick={handleUserIconClick} onMouseEnter={() => isLoggedIn && setShowUserPopup(true)} />
              {isLoggedIn && showUserPopup && (
                <div className="user-hover-menu">
                  <div className="user-popup-title">Welcome, {userProfile?.name || userProfile?.email || 'User'}</div>
                  <Link to="/account/profile" onClick={() => setShowUserPopup(false)}><span>Profile</span></Link>
                  <Link to="/account/orders" onClick={handleOrderLinkClick} className="order-link"><span>Orders</span></Link>
                  <div className="logout-item" onClick={handleLogout}><span>Logout</span></div>
                </div>
              )}
            </div>
            <div className="cart-icon-wrapper" onClick={openCart}>
              <BsBagDash className="icon cart-icon-header" />
              {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
              {isSyncingCart && <span className="cart-syncing-indicator">Syncing...</span>}
            </div>
          </div>
        </nav>
      </header>

      <Currency open={currencyOpen} onClose={() => setCurrencyOpen(false)} selectedCountry={selectedCountry} />

      <aside className={`shop-drawer ${drawerOpen ? "open" : ""}`}>
        {drawerMode === 'main' ? (
          <>
            <div className="drawer-top">
              <h3>Shop by</h3>
              <XMarkIcon className="drawer-close" onClick={handleDrawerClose} />
            </div>
            <ul className="drawer-main-links">
              <li className="drawer-simple">
                <div className="drawer-simple-content" onClick={handleHomeClick}><span>Home</span></div>
              </li>
              {categories.map(category => (
                <li key={category.id} className={`drawer-item ${expandedCategory === category.id ? "active" : ""}`}>
                  <div className="drawer-link" onClick={() => {
                    if (subcategories[category.id]?.length > 0) handleCategoryExpand(category.id);
                    else { navigate(`/products?category=${category.slug}`); handleDrawerClose(); }
                  }}>
                    <span className="drawer-link-text">{category.name}</span>
                    {subcategories[category.id]?.length > 0 && <PlusIcon active={expandedCategory === category.id} />}
                  </div>
                  {expandedCategory === category.id && subcategories[category.id]?.length > 0 && (
                    <div className="shop-categories-wrapper">
                      <ul className="categories-list">
                        {subcategories[category.id].map(sub => (
                          <li key={sub.id} className="subcategory-item">
                            <Link
                              to={`/products?category=${category.slug}&subcategory=${sub.slug}`}
                              onClick={handleDrawerClose}
                              className="subcategory-link"
                            >
                              <span className="subcategory-text">{sub.name}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </li>
              ))}
              <li className={`drawer-item ${bestsellerOpen ? "active" : ""}`}>
                <div className="drawer-link" onClick={() => { setBestsellerOpen(!bestsellerOpen); setExpandedCategory(null); }}>
                  <span className="drawer-link-text">Bestseller</span>
                  <PlusIcon active={bestsellerOpen} />
                </div>
                {bestsellerOpen && (
                  <div className="bestseller-products">
                    <div className="view-all" onClick={handleViewAllBestsellers}>
                      <span>View all Bestsellers</span>
                    </div>
                    <div className="bestseller-grid">
                      {loadingBestsellers ? (
                        <div className="loading-bestsellers">Loading bestsellers...</div>
                      ) : bestsellerProducts.length > 0 ? (
                        bestsellerProducts.map(p => (
                          <div key={p.id} className="bestseller-card" onClick={() => handleBestsellerProductClick(p)}>
                            <div className="bestseller-img-wrap">
                              <img
                                src={p.img ? getCompleteImageUrl(p.img) : DEFAULT_IMAGE}
                                alt={p.name}
                                onError={(e) => { e.target.onerror = null; e.target.src = DEFAULT_IMAGE; }}
                              />
                            </div>
                            <p className="bestseller-name">{p.name}</p>
                            {p.price > 0 && <p className="bestseller-price">{formatPrice(p.price)}</p>}
                          </div>
                        ))
                      ) : (
                        <div className="no-bestsellers">No bestseller products found</div>
                      )}
                    </div>
                  </div>
                )}
              </li>
              <li className="drawer-simple">
                <div className="drawer-simple-content" onClick={() => { navigate("/products"); handleDrawerClose(); }}>
                  <span>Sale</span>
                </div>
              </li>
            </ul>
            <div className="drawer-bottom">
              {isLoggedIn ? (
                <div className="drawer-user-info">
                  <span>Welcome, {userProfile?.name || 'User'}</span>
                  <button className="drawer-logout-btn" onClick={handleLogout}>Logout</button>
                </div>
              ) : (
                <Link to="/login" className="drawer-bottom-link"><span>Log in</span></Link>
              )}
              <Link to="/contact" className="drawer-bottom-link"><span>Contact</span></Link>
              {renderDrawerWishlistLink()}
              <div className="drawer-bottom-link currency-bottom-link" onClick={handleCurrencyClick}>
                <div className="drawer-currency-display">
                  <img
                    src={getFlagImageUrl(selectedCountry)}
                    alt={selectedCountry?.countryName || selectedCountry?.countryCode || 'Currency'}
                    className="drawer-currency-flag"
                    onError={(e) => { e.target.onerror = null; e.target.src = "/images/flags/default.svg"; }}
                  />
                  <span className="drawer-currency-code">{selectedCountry?.currency?.symbol || selectedCountry?.currency?.code}</span>
                  <FaChevronDown className="drawer-currency-arrow" />
                </div>
              </div>
            </div>
          </>
        ) : drawerMode === 'category' && selectedCategory ? (
          <>
            <div className="drawer-top">
              <button className="drawer-back-btn" onClick={handleBackToMain}><FaChevronLeft /></button>
              <h3>{selectedCategory.name}</h3>
              <XMarkIcon className="drawer-close" onClick={handleDrawerClose} />
            </div>
            <div className="category-submenu">
              <div className="category-submenu-content">
                {subcategories[selectedCategory.id]?.length > 0 ? (
                  <ul className="subcategories-list-full">
                    {subcategories[selectedCategory.id].map(sub => (
                      <li key={sub.id} className="subcategory-item-full">
                        <Link
                          to={`/products?category=${selectedCategory.slug}&subcategory=${sub.slug}`}
                          onClick={handleDrawerClose}
                          className="subcategory-link-full"
                        >
                          <span className="subcategory-text-full">{sub.name}</span>
                          <FaChevronRight className="subcategory-arrow" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="no-subcategories-message">
                    <p>No subcategories available for {selectedCategory.name}</p>
                    <Link
                      to={`/products?category=${selectedCategory.slug}`}
                      className="view-category-btn"
                      onClick={handleDrawerClose}
                    >
                      View {selectedCategory.name} Products
                    </Link>
                  </div>
                )}
              </div>
            </div>
            <div className="drawer-bottom">
              <div className="drawer-bottom-link" onClick={handleBackToMain}>
                <FaChevronLeft style={{ marginRight: '8px' }} />
                <span>Back to Menu</span>
              </div>
            </div>
          </>
        ) : null}
      </aside>
      {drawerOpen && <div className="drawer-overlay" onClick={handleDrawerClose} />}

      <aside className={`cart-drawer ${isCartOpen ? "open" : ""}`}>
        <div className="cart-header">
          <h3>Your cart ({cartCount})</h3>
          <XMarkIcon className="cart-close" onClick={closeCart} />
        </div>
        <div className="cart-body">
          {cartLoading ? (
            <div className="cart-loading"><div className="loading-spinner"></div><p>Loading your cart...</p></div>
          ) : isSyncingCart ? (
            <div className="cart-syncing"><div className="loading-spinner"></div><p>Syncing your cart...</p></div>
          ) : cartItems.length === 0 ? (
            <div className="cart-empty">
              <div className="cart-bag-icon"><BsBagDash className="cart-empty-icon" /></div>
              <p>Your cart is empty</p>
              <button className="cart-continue-btn" onClick={closeCart}>CONTINUE SHOPPING</button>
              <div className="cart-login">
                <span className="cart-login-text">
                  Have an account?
                  <Link to="/login" className="cart-login-text cart-login-link">Log in</Link>
                </span>
              </div>
            </div>
          ) : (
            <>
              {/* Add to Cart Success Message */}
              {showAddToCartMessage && (
                <div className="add-to-cart-success-message">
                  {addToCartMessage}
                </div>
              )}
              
              {showClearCartConfirm && (
                <div className="delete-confirm-modal-inline">
                  <div className="delete-confirm-content">
                    <div className="delete-confirm-header-inline"><h4>Clear Cart</h4></div>
                    <div className="delete-confirm-body-inline">
                      <p>Are you sure you want to clear your entire cart? This action cannot be undone.</p>
                    </div>
                    <div className="delete-confirm-footer-inline">
                      <button className="delete-confirm-cancel-inline" onClick={cancelClearCart}>Cancel</button>
                      <button className="delete-confirm-remove-inline" onClick={confirmClearCart} disabled={isClearingCart}>
                        {isClearingCart ? 'Clearing...' : 'Clear Cart'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
              <div className="cart-full">
                <div className="clear-cart-btn-wrapper">
                  <button className="clear-cart-btn" onClick={handleClearCartDrawer} disabled={isClearingCart}>
                    <ClearCartIcon className="clear-cart-icon" />
                    <span>{isClearingCart ? 'Clearing Cart...' : 'Clear Cart'}</span>
                  </button>
                </div>
                {subtotal >= 52.05 ? (
                  <div className="shipping-unlocked-simple"><span>You have unlocked free shipping!</span></div>
                ) : (
                  <div className="shipping-progress-simple">
                    <div className="progress-text-simple">
                      Spend {formatPrice(shippingProgress.remaining)} more for FREE shipping!
                    </div>
                    <div className="progress-bar-simple">
                      <div className="progress-fill-simple" style={{ width: `${shippingProgress.progress}%` }} />
                    </div>
                  </div>
                )}
                <div className="cart-divider" />
                <div className="cart-items-simple">
                  {cartItems.map((item) => {
                    const quantity = item.qty || 1;
                    const uniqueId = item.uniqueId || item.id;
                    let itemImage;
                    if (item.image) itemImage = item.image;
                    else if (item.img) itemImage = item.img;
                    else if (item.img1) itemImage = item.img1;
                    return (
                      <div key={uniqueId} className="cart-item-simple">
                        <div className="cart-item-image-simple" onClick={() => handleCartItemImageClick(item)} title="View product details">
                          <img src={getCompleteImageUrl(itemImage)} alt={item.name || item.title || "Product"} onError={(e) => { e.target.onerror = null; e.target.src = DEFAULT_IMAGE; }} />
                        </div>
                        <div className="cart-item-details-simple">
                          <h4 className="cart-item-name-simple">{item.name || item.title || "Product"}</h4>
                          {(item.selectedSize || item.selectedFragrance) && (
                            <div className="cart-item-attributes-simple">
                              {item.selectedSize && <span className="attribute-badge">Size: {item.selectedSize}</span>}
                              {item.selectedFragrance && <span className="attribute-badge">Fragrance: {item.selectedFragrance}</span>}
                            </div>
                          )}
                          <div className="cart-item-bottom-row">
                            <div className="cart-item-left-controls">
                              <div className="cart-item-quantity-simple">
                                <button className="quantity-btn-simple" onClick={() => handleDecreaseQty(uniqueId)} disabled={quantity <= 1} aria-label="Decrease quantity">
                                  <MinusIcon />
                                </button>
                                <span className="quantity-number-simple">{quantity}</span>
                                <button className="quantity-btn-simple" onClick={() => handleIncreaseQty(uniqueId)} aria-label="Increase quantity">
                                  <PlusQtyIcon />
                                </button>
                              </div>
                              <button className="cart-item-delete" onClick={(e) => { e.stopPropagation(); handleRemoveFromCart(uniqueId); }} aria-label="Remove item">
                                <TrashIcon />
                              </button>
                            </div>
                            <p className="cart-item-price-simple">{formatPrice(item.basePrice)}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="cart-divider" />

                <div className="cart-also-like-wrapper">
                  <div className="cart-also-like-header" onClick={handleAlsoLikeToggle}>
                    <span>You might also like</span>
                    <button className="cart-also-like-toggle" aria-label="Toggle recommendations">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16" height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{
                          transform: showAlsoLike ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.3s ease'
                        }}
                      >
                        <polyline points="18 15 12 9 6 15" />
                      </svg>
                    </button>
                  </div>

                  <div className={`cart-also-like-popup ${showAlsoLike ? 'also-like-visible' : 'also-like-hidden'}`}>
                    {alsoLikeLoading ? (
                      <div className="also-like-loading">
                        <div className="loading-spinner-small" />
                        <span>Loading recommendations...</span>
                      </div>
                    ) : (
                      <div className="also-like-carousel-container">
                        <button
                          className="carousel-arrow carousel-arrow-left"
                          onClick={scrollCarouselLeft}
                          aria-label="Previous"
                          style={{
                            opacity: canScrollLeft ? 1 : 0,
                            pointerEvents: canScrollLeft ? 'auto' : 'none'
                          }}
                        >
                          <FaChevronLeft />
                        </button>

                        <div
                          className="also-like-products-carousel"
                          ref={carouselRef}
                          onScroll={checkCarouselScroll}
                        >
                          {alsoLikeProducts.map((product) => (
                            <div
                              key={product.id}
                              className="also-like-product-card"
                              onClick={() => handleAlsoLikeProductClick(product)}
                            >
                              <div className="also-like-product-image">
                                <img
                                  src={getCompleteImageUrl(product.image)}
                                  alt={product.name}
                                  onError={(e) => { e.target.onerror = null; e.target.src = DEFAULT_IMAGE; }}
                                />
                              </div>
                              <div className="also-like-product-info">
                                {product.brand && (
                                  <p className="also-like-product-brand">{product.brand.toUpperCase()}</p>
                                )}
                                <p className="also-like-product-name">{product.name}</p>
                                {renderAlsoLikePrice(product)}
                              </div>
                              <button
                                className="also-like-add-to-cart"
                                onClick={(e) => handleAddToCartFromAlsoLike(product, e)}
                                aria-label="Add to cart"
                              >
                                <BsBagDash />
                              </button>
                            </div>
                          ))}
                        </div>

                        <button
                          className="carousel-arrow carousel-arrow-right"
                          onClick={scrollCarouselRight}
                          aria-label="Next"
                          style={{
                            opacity: canScrollRight ? 1 : 0,
                            pointerEvents: canScrollRight ? 'auto' : 'none'
                          }}
                        >
                          <FaChevronRight />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="cart-footer-section">
                  <div className="cart-divider" />
                  <div className="cart-options-row">
                    <label className="cart-gift-label" htmlFor="wrap-gift-cart">
                      <input type="checkbox" id="wrap-gift-cart" className="cart-option-checkbox" />
                      <span className="cart-option-text">
                        <GiftIcon /> Wrap as a gift <span className="gift-emoji">🎁</span>
                      </span>
                    </label>
                    <button className="cart-add-note-btn" onClick={handleAddOrderNoteClick} type="button">
                      {showOrderNoteInput ? 'Cancel note' : savedOrderNote ? 'Edit order note' : 'Add order note'}
                    </button>
                  </div>
                  {showOrderNoteInput && (
                    <div className="order-note-container">
                      <div className="order-note-input-wrapper">
                        <textarea className="order-note-textarea" placeholder="How can we help you?" value={orderNoteText} onChange={handleOrderNoteChange} rows="3" autoFocus />
                        <button className="order-note-save-btn" onClick={handleSaveOrderNote} type="button">SAVE</button>
                        {showSaveMessage && (
                          <div className={`order-note-message ${saveMessage.includes("saved") ? "success" : "error"}`}>
                            {saveMessage}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {savedOrderNote && !showOrderNoteInput && (
                    <div className="cart-saved-note">
                      <span className="saved-note-label">Note:</span>
                      <span className="saved-note-text">{savedOrderNote}</span>
                    </div>
                  )}
                  <div className="cart-divider" />
                  <div className="cart-summary-simple">
                    <div className="summary-row-simple">
                      <span>Subtotal</span>
                      <span className="summary-price">{formatPrice(subtotal)} INR</span>
                    </div>
                    <p className="summary-note-simple">Shipping & taxes included</p>
                    <button className="cart-checkout-btn-simple" onClick={handleCheckout}>🔒 CHECK OUT</button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </aside>
      {isCartOpen && <div className="cart-overlay" onClick={closeCart} />}
      
      {/* Add CSS for success message */}
      <style>{`
        .add-to-cart-success-message {
          position: fixed;
          top: 20px;
          right: 20px;
          background-color: #4CAF50;
          color: white;
          padding: 12px 20px;
          border-radius: 8px;
          z-index: 10000;
          animation: slideIn 0.3s ease-out;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }
        
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </>
  );
}

export default Header;